<template>
  <el-dialog
    title="支付通道编辑"
    :visible="visible"
    width="600px"
    :close-on-click-modal="false"
    class="page-dialog dialog-fixed-height"
    @close="handleClose"
  >
    <div class="dialog-content">
      <div class="form-section-title">配置信息</div>
      <el-form
        ref="formRef"
        :model="localForm"
        :rules="formRules"
        label-position="right"
        label-width="120px"
        size="small"
        class="channel-form"
      >
        <el-form-item label="通道名称" prop="channel_name">
          <el-input
            v-model="localForm.channel_name"
            placeholder="请输入通道名称"
            maxlength="100"
            show-word-limit
            style="width: 280px"
            disabled
          />
        </el-form-item>
        <el-form-item label="支持币种" prop="currency">
          <el-input disabled :value="localForm.currency" style="width: 140px" />
        </el-form-item>
        <el-form-item label="单笔限额" prop="limit_min" v-if="channelType === 'payin'">
          <span class="limit-inputs">
            <el-input-number
              v-model="localForm.payin_min_amount"
              :min="0"
              :controls="false"
              class="input-number-sm"
              placeholder="最小"
            />
            <span class="limit-sep">-</span>
            <el-input-number
              v-model="localForm.payin_max_amount"
              :min="0"
              :controls="false"
              class="input-number-sm"
              placeholder="最大"
            />
            <span class="form-unit">{{ localForm.currency }}</span>
          </span>
        </el-form-item>
        <el-form-item label="单笔限额" prop="limit_min" v-if="channelType === 'payout'">
          <span class="limit-inputs">
            <el-input-number
              v-model="localForm.payout_min_amount"
              :min="0"
              :controls="false"
              class="input-number-sm"
              placeholder="最小"
            />
            <span class="limit-sep">-</span>
            <el-input-number
              v-model="localForm.payout_max_amount"
              :min="0"
              :controls="false"
              class="input-number-sm"
              placeholder="最大"
            />
            <span class="form-unit">{{ localForm.currency }}</span>
          </span>
        </el-form-item>
        <el-form-item v-if="channelType === 'payin'" label="充值手续费" prop="payin_fee_rate">
          <el-input v-model="localForm.payin_fee_rate" :controls="false" class="input-number-sm" disabled />
          <span class="form-unit">%</span>
        </el-form-item>
        <el-form-item v-if="channelType === 'payout'" label="提现手续费" prop="payout_fee_rate">
          <el-input-number v-model="localForm.payout_fee_rate" :controls="false" class="input-number-sm" disabled />
          <span class="form-unit">%</span>
        </el-form-item>
        <el-form-item label="权重" prop="weight">
          <el-input-number
            v-model="localForm.weight"
            :min="0"
            :controls="false"
            class="input-number-sm"
            placeholder="0"
            :step="1"
            step-strictly
          />
          <span class="form-hint">数字越大权重越高</span>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="localForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="2">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="适用对象" prop="vips" class="form-item-vips" v-if="channelType === 'payin'">
          <div class="vips-wrap">
            <el-checkbox :value="isAllVipChecked" @change="handleAllVipChange">全选</el-checkbox>
            <el-checkbox-group v-model="localForm.vips" class="vips-group">
              <el-checkbox v-for="item in vipOptions" :key="item.value" :label="item.value">
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </el-form-item>
        <el-form-item label="备注信息" prop="remark">
          <el-input
            v-model="localForm.remark"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入备注"
            style="width: 320px"
          />
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">确认</el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, computed, getCurrentInstance } from 'vue'
import { updatePayinChannel, updatePayoutChannel } from '@/api/finance/paymentChannel'
import { yuanToCents, centsToYuan } from '@/utils/amount'
import { useVipOptions } from '@/composables/useVipOptions'
import config from '@/config'

const props = defineProps({
  visible: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },
  channelType: { type: String, default: 'payin' },
  editId: { type: [Number, String], default: null },
  editRow: { type: Object, default: null },
})

const emit = defineEmits(['update:visible', 'success'])

const { proxy } = getCurrentInstance()
const formRef = ref(null)
const loading = ref(false)
const { vipOptions } = useVipOptions()
const channelType = computed(() => props.channelType)

/* 
 "channel_name": "string",
        "channel_no": "string",
        "currency": "string",
        "fix_amount": "string",
        "id": 0,
        "payin_base_fee": 0,
        "payin_fee_rate": 0,
        "payin_max_amount": 0,
        "payin_min_amount": 0,
        "remark": "string",
        "status": 0,
        "vips": "string",
        "weight": 0
*/
const localForm = reactive({
  id: null,
  channel_name: '',
  currency: config.currency,
  payin_min_amount: null,
  payin_max_amount: null,
  payout_min_amount: null,
  payout_max_amount: null,
  fix_amount: '',
  payin_fee_rate: null,
  payout_fee_rate: null,
  payout_base_fee: null,
  payout_fee_rate: null,
  weight: 0,
  status: 1,
  vips: [],
  remark: '',
})

const isAllVipChecked = computed(() => localForm.vips.length === (vipOptions.value?.length || 0))

const formRules = {
  status: [{ required: true, message: '请选择状态', trigger: 'change' }],
}

function parseVips(v) {
  if (v == null || v === '') return []
  if (Array.isArray(v)) return v
  return String(v)
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !Number.isNaN(n))
}

const payloadKeys = computed(() => {
  return Object.keys({ ...(props.editRow || {}) })
})
const centsKeys = ['payin_min_amount', 'payin_max_amount', 'payout_min_amount', 'payout_max_amount']
function buildPayload() {
  const payload = {}
  payloadKeys.value.forEach(key => {
    if (centsKeys.includes(key)) {
      payload[key] = yuanToCents(localForm[key] || props.editRow[key])
    } else {
      payload[key] = localForm[key] !== undefined ? localForm[key] : props.editRow[key]
    }
    if (key === 'vips') {
      payload[key] = Array.isArray(localForm.vips) ? localForm.vips.sort((a, b) => a - b).join(',') : ''
    }
  })
  if (localForm.id != null) payload.id = localForm.id

  return payload
}

function resetForm() {
  Object.assign(localForm, {
    id: null,
    channel_name: '',
    currency: config.currency,
    payin_min_amount: null,
    payin_max_amount: null,
    payout_min_amount: null,
    payout_max_amount: null,
    fix_amount: '',
    payin_fee_rate: null,
    payout_fee_rate: null,
    payout_base_fee: null,
    payout_fee_rate: null,
    weight: 0,
    status: 1,
    vips: [],
    remark: '',
  })
}

function fillFormFromRow(data) {
  if (!data) return

  Object.assign(localForm, {
    id: data.id,
    channel_name: data.channel_name ?? data.name ?? '',
    currency: data.currency ?? config.currency,
    payin_min_amount: data.payin_min_amount / 100 ?? null,
    payin_max_amount: data.payin_max_amount / 100 ?? null,
    payout_min_amount: data.payout_min_amount / 100 ?? null,
    payout_max_amount: data.payout_max_amount / 100 ?? null,
    fix_amount: data.fix_amount ?? data.fixAmount ?? '',
    payin_fee_rate: data.payin_fee_rate ?? null,
    payin_base_fee: data.payin_base_fee ?? null,
    payout_base_fee: data.payout_base_fee ?? null,
    payout_fee_rate: data.payout_fee_rate ?? null,
    weight: data.weight ?? 0,
    status: data.status ?? 1,
    vips: parseVips(data.vips ?? data.vip_limit),
    remark: data.remark ?? '',
  })
}

watch(
  () => [props.visible, props.mode, props.channelType, props.editId, props.editRow],
  () => {
    if (props.visible) {
      resetForm()
      if (props.mode === 'edit' && props.editRow) {
        fillFormFromRow(props.editRow)
      }
    }
  },
  { immediate: true }
)

function handleAllVipChange(checked) {
  localForm.vips = checked ? (vipOptions.value || []).map(o => o.value) : []
}

function handleClose() {
  emit('update:visible', false)
}

function handleConfirm() {
  formRef.value.validate(valid => {
    if (!valid) return
    loading.value = true
    const payload = buildPayload()
    const isPayin = props.channelType === 'payin'
    const api = isPayin ? updatePayinChannel : updatePayoutChannel
    api(payload)
      .then(res => {
        if (res && res.code !== undefined && res.code !== 0) throw new Error(res.msg || res.message || '保存失败')
        proxy.$message.success('保存成功')
        emit('success')
        handleClose()
      })
      .catch(err => {
        proxy.$message.error(err?.response?.data?.message || err.message || '保存失败')
      })
      .finally(() => {
        loading.value = false
      })
  })
}
</script>

<style lang="scss" scoped>
.form-section-title {
  font-weight: 600;
  margin-bottom: 16px;
  font-size: 14px;
}
.channel-form ::v-deep .el-form-item {
  margin-bottom: 18px;
}
.input-number-sm {
  width: 140px;
}
.channel-form ::v-deep .input-number-sm.el-input-number {
  width: 140px;
}
.limit-inputs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.limit-sep {
  color: #909399;
}
.form-unit {
  margin-left: 4px;
  color: #606266;
  font-size: 12px;
}
.form-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
.vips-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vips-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}
.dialog-fixed-height ::v-deep .el-dialog__body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
